import { Component, computed, effect, forwardRef, inject, input, output, signal, untracked } from "@angular/core";
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from "@angular/forms";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { NgbTimepickerConfig, NgbTimepickerModule, NgbTimeStruct } from "@ng-bootstrap/ng-bootstrap";

import { AdaaHelper } from "../../../../core/utils";
import type { ParameterCatalog } from "../../../models";
import { LanguageService } from "../../../services";

@Component({
  selector: "adaa-form-input-time",
  imports: [FormsModule, ReactiveFormsModule, NgbTimepickerModule, FontAwesomeModule],
  templateUrl: "./form-input-time.html",
  styleUrl: "./form-input-time.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FormInputTimeComponent),
    },
  ],
})
export class FormInputTimeComponent implements ControlValueAccessor {
  readonly AdaaHelper = AdaaHelper;
  readonly languageService = inject(LanguageService);
  readonly inputControl = new FormControl();

  readonly time = signal<NgbTimeStruct | undefined>(undefined);

  private readonly _requiredFromValidator = signal<boolean>(false);
  private readonly _disabledByFormControl = signal<boolean>(false);
  private _onChange = (_time: NgbTimeStruct) => {};
  private _onTouched = () => {};

  readonly label = input<string>("");
  readonly invalid = input<boolean>(false);
  readonly controlName = input<string | null>(null);
  readonly controlId = input<string | null>(null);
  readonly required = input<boolean>(false);
  readonly isDisabled = input<boolean>(false);
  readonly setValidator = input<{ parameterCatalogs: ParameterCatalog[] }>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly inputChanges = output<any>();

  readonly effectiveControlId = computed(() => this.controlId() ?? this.controlName());
  readonly isRequired = computed(() => this.required() || this._requiredFromValidator());
  readonly effectivelyDisabled = computed(() => this.isDisabled() || this._disabledByFormControl());

  constructor() {
    const config = inject(NgbTimepickerConfig);
    config.spinners = false;
    config.meridian = true;

    effect(() => {
      const validatorList = this.setValidator();
      const name = this.controlName();
      if (validatorList?.parameterCatalogs) {
        const field = validatorList.parameterCatalogs.find((e) => e.fieldName === name);
        if (field) {
          untracked(() => {
            this._requiredFromValidator.set(field.mandatory === "Y");
          });
        }
      }
    });
  }

  public writeValue(time: NgbTimeStruct): void {
    if (time) {
      this.time.set(time);
    }
  }

  public registerOnChange(fn: (time: NgbTimeStruct) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this._disabledByFormControl.set(isDisabled);
  }

  public onTimeChange(time: NgbTimeStruct): void {
    this.time.set(time);
    this._onChange(time);
    this._onTouched();
  }

  public valueChanged(time: NgbTimeStruct): void {
    this.time.set(time);
    this._onChange(time);
    this._onTouched();
    this.inputChanges.emit(time);
  }
}
